import { useCallback, useEffect, useState } from "react";
import { ReQcPanel } from "../../components/ReQcPanel.js";
import { useAuth } from "../../auth/AuthContext.js";
import { api, ApiError } from "../../api/client.js";
import { ArtifactIdentity } from "../../components/ArtifactIdentity.js";
import { PersonaSourceStillProgressCard } from "../../components/PersonaSourceStillProgressCard.js";
import { VideoGateProgressCard } from "../../components/VideoGateProgressCard.js";
import { useForegroundResume } from "../../hooks/useForegroundResume.js";
import { useReviewEventStream } from "../../hooks/useReviewEventStream.js";
import { ImageRequestsList } from "./image/ImageRequestsList.js";
import { ReviewsAutoRunBar } from "./ReviewsAutoRunBar.js";
import { ReviewsTelegramBar } from "./ReviewsTelegramBar.js";
import { ReviewsIgAutopostBar } from "./ReviewsIgAutopostBar.js";
import { ReviewsReAutoSwitch, ReviewsTelegramAutoSwitch } from "./ReviewsAutomationSwitches.js";
import type { GateTwoReviewPayload, OutputCandidatesResponse, PersonaSourceStillSummary, VideoHistoryRow, VideoProgress } from "../../api/types.js";

/**
 * P0 UX fix (2026-09): these are the exact non-terminal Gate 1 statuses
 * genuinely reachable without a client request driving them — the SAME
 * list `persona-source-still-automation.ts` scans on the backend. A real
 * in-flight artifact must never be invisible just because it hasn't
 * reached `persona_swap_ready_for_review` yet.
 */
const GATE1_PROCESSING_STATUSES = [
  "persona_swap_preprocessing_pending",
  "persona_swap_pending",
  // Gate 1 text-cleanup failed - waits for the user's decision (Retry cleanup / Remove); it must stay visible here, not vanish with the processing list.
  "persona_swap_text_cleanup_failed",
  "persona_swap_text_cleanup_submission_uncertain",
  "persona_swap_submission_uncertain",
  "persona_swap_visual_modification_pending",
  // Live-reproduced UX bug (2026-09-05, real paid E2E run, artifact
  // 9048c5b4-...): the backend has genuinely handled this status since
  // migration 70 (FIX-010 pendant-removal normalization — see
  // `persona-source-still-automation.ts`'s own `reconcilePendantRemovalPolling`),
  // but it was never added HERE, so an artifact actively running a real
  // paid pendant-removal edit call was invisible on REVIEWS for the
  // entire duration of that call (confirmed live: ~3 minutes) — not a
  // polling-speed issue, a genuinely missing status in this list.
  "persona_swap_pendant_removal_pending",
].join(",");

/**
 * FIX-010 correction (2026-09): `persona_swap_visual_modification_failed`
 * and `persona_swap_visual_modification_submission_uncertain` are no
 * longer permanent dead ends when a real, already-produced pre-
 * modification image (`swappedFrameGcsUri`) exists — `DecidePersonaSourceStillReview`
 * now accepts both as reviewable (see its own doc comment). Fetched
 * alongside the ordinary `persona_swap_ready_for_review` bucket (moved
 * OUT of `GATE1_PROCESSING_STATUSES` above, since these two are no longer
 * "nothing to do yet") so the existing image reaches the USER with a
 * truthful warning instead of being silently invisible.
 */
const GATE1_REVIEWABLE_WITH_WARNING_STATUSES = ["persona_swap_ready_for_review", "persona_swap_visual_modification_failed", "persona_swap_visual_modification_submission_uncertain"].join(",");

/**
 * FIX-010 correction, item 7 (2026-09): a still-edit provider call
 * returning MORE THAN ONE output is neither "ready for review" (no
 * decision about which candidate continues has been made yet) nor
 * ordinary "processing" (nothing produced yet) — its own distinct
 * status/bucket, mirroring the video-level multi-output candidates card.
 */
const GATE1_CANDIDATES_PENDING_STATUS = "persona_swap_visual_modification_candidates_pending";

/**
 * Gate 1 Manual Source Frame Picker (2026-09-16): `source_frame_extracted`
 * is deliberately its OWN bucket, never folded into
 * `GATE1_PROCESSING_STATUSES` above — it is a manual-ACTION state (the
 * user must retry or pick a frame), not a background-processing one
 * (nothing is currently running on it). Mirrors
 * `GATE1_CANDIDATES_PENDING_STATUS`'s own "distinct bucket for a
 * distinct kind of pending decision" precedent.
 */
const GATE1_NEEDS_FRAME_SELECTION_STATUS = "source_frame_extracted";

// Real-time push (`useReviewEventStream`, now shared with HISTORY — see
// apps/dashboard/src/hooks/useReviewEventStream.ts) is the PRIMARY way this
// list learns about a change — this interval is only the safety net for
// when that connection is down (network blip, server restart, or this
// deployment not having wired a reviewEventBus at all), so it no longer
// needs to be fast. Was 5000ms when polling was the only mechanism.
/** Two-fixes-follow-up (2026-09, refresh-cadence correction): product requirement is exactly 15s auto-refresh for Gate 1/Gate 2/Final Review active status — was 30s, silently doubling how stale a displayed provider status could appear even while the provider itself is responsive. */
const GATE1_REFRESH_INTERVAL_MS = 15_000;

/**
 * REVIEWS-as-action-queue guarantee, Gate 1 "approved" side (2026-09
 * follow-up, evidenced against a real leaked-historical-artifact defect):
 * pure bucketing over the SINGLE truthful `activeOnly=true` fetch — no
 * second, unfiltered fetch, no client-side heuristic. The server's own
 * `activeOnly` predicate (`persona-source-still-routes.ts`'s `handleList`)
 * is the ONE place "active" is decided; this function only splits that
 * already-truthful set into the two REVIEWS sub-sections by a real,
 * durable field (`gate2AutoStartVideoId`) — never re-derives activeness
 * itself. Unit-testable without a DOM.
 */
export function selectGate1ApprovedBuckets(activeApproved: readonly PersonaSourceStillSummary[]): {
  readonly starting: readonly PersonaSourceStillSummary[];
  readonly activeWorkflow: readonly PersonaSourceStillSummary[];
} {
  return {
    starting: activeApproved.filter((item) => item.gate2AutoStartVideoId === null),
    activeWorkflow: activeApproved.filter((item) => item.gate2AutoStartVideoId !== null),
  };
}

/**
 * Gate 1 identity-failed items (2026-09-19): a finished result whose FINAL
 * cleaned Master failed identity verification (`progress.identityCheckFailed`,
 * derived on the backend from `cleanedMasterIdentityPreserved === false`)
 * must not be presented as an ordinary, successful "Ready for review"
 * candidate — and can never be approved. It is split OUT of that list into
 * its own, explicitly-labelled section — never dropped: both lists together
 * are exactly the input, so nothing produced ever stops reaching the
 * reviewer. Pure and unit-testable without a DOM.
 */
export function splitGate1ReadyByIdentity(readyItems: readonly PersonaSourceStillSummary[]): {
  readonly normal: readonly PersonaSourceStillSummary[];
  readonly identityFailed: readonly PersonaSourceStillSummary[];
} {
  return {
    normal: readyItems.filter((item) => !item.progress.identityCheckFailed),
    identityFailed: readyItems.filter((item) => item.progress.identityCheckFailed),
  };
}

/**
 * FIX-010 correction, item 7 (2026-09): when a Gate 1 still-edit provider
 * call returns more than one output, none is ever auto-selected or
 * auto-rejected — this card is the ONLY place that anomaly becomes
 * visible and actionable, directly inside the existing Gate 1 Reviews
 * section (mirrors the video-level `MultiOutputCandidatesCard` exactly).
 * Every candidate is independently previewable so the user can actually
 * look at each option before choosing — "Use this image" sends the
 * caller's own `candidateIndex` verbatim, never a guessed one. This is
 * deliberately NOT Gate 1 approval: selecting a candidate only resolves
 * WHICH provider output becomes this stage's canonical image; the
 * unmodified existing pipeline (identity guard + per-target verification
 * -> ready_for_review -> the ordinary Gate 1 approve/reject) still runs
 * afterward, untouched by this component.
 */
function VisualModificationCandidatesCard({
  tenantId,
  item,
  onDecided,
}: {
  readonly tenantId: string;
  readonly item: PersonaSourceStillSummary;
  readonly onDecided: () => Promise<void>;
}) {
  const { user, csrfToken } = useAuth();
  const [candidates, setCandidates] = useState<readonly { readonly index: number; readonly sizeBytes: number }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ candidates: readonly { readonly index: number; readonly sizeBytes: number }[] }>(`/videos/${tenantId}/persona-source-stills/${item.id}/visual-modification-candidates`)
      .then((result) => {
        if (!cancelled) setCandidates(result.candidates);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId, item.id]);

  async function decide(body: { readonly decision: "select"; readonly candidateIndex: number } | { readonly decision: "reject" }): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/videos/${tenantId}/persona-source-stills/${item.id}/visual-modification-candidates/decide`, csrfToken, { ...body, reviewedBy: user?.email ?? "owner" });
      // Never assume success from the click alone — re-read durable backend state.
      await onDecided();
    } catch (decideError) {
      setError(decideError instanceof ApiError ? decideError.message : "Decision failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" data-testid={`visual-modification-candidates-${item.id}`}>
      <h3>{item.id} — multiple edited images produced</h3>
      <p className="muted">The still-edit provider returned more than one result. Look at each option, then choose which one continues, or reject all.</p>
      {error && <div className="error-banner">{error}</div>}
      <div className="grid grid-3">
        {candidates.map((candidate) => (
          <div key={candidate.index} style={{ marginBottom: 8 }}>
            <p>{`Option ${candidate.index + 1}`}</p>
            <img
              className="preview-image"
              src={`/videos/${tenantId}/persona-source-stills/${item.id}/visual-modification-candidates/${candidate.index}/preview`}
              alt={`Candidate ${candidate.index + 1}`}
              data-testid={`visual-modification-candidate-preview-${item.id}-${candidate.index}`}
            />
            <p className="muted">{candidate.sizeBytes} bytes</p>
            <button className="success" disabled={busy} onClick={() => void decide({ decision: "select", candidateIndex: candidate.index })}>
              Use this image
            </button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <button className="danger" disabled={busy} onClick={() => void decide({ decision: "reject" })}>
          Reject all
        </button>
      </div>
    </div>
  );
}

function Gate1Section({ tenantId }: { readonly tenantId: string }) {
  const { user, csrfToken } = useAuth();
  const [processingItems, setProcessingItems] = useState<PersonaSourceStillSummary[]>([]);
  const [needsFrameSelectionItems, setNeedsFrameSelectionItems] = useState<PersonaSourceStillSummary[]>([]);
  const [candidatesPendingItems, setCandidatesPendingItems] = useState<PersonaSourceStillSummary[]>([]);
  const [readyItems, setReadyItems] = useState<PersonaSourceStillSummary[]>([]);
  // Gate 1 results the user rejected that still have their candidate snapshot (2026-09-25): a rejection must not make the video vanish —
  // the user decides between "Choose another frame" (run again) and Remove.
  const [rejectedItems, setRejectedItems] = useState<PersonaSourceStillSummary[]>([]);
  const [approvedItems, setApprovedItems] = useState<readonly PersonaSourceStillSummary[]>([]);
  const [startingItems, setStartingItems] = useState<readonly PersonaSourceStillSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Truthful freshness correction (2026-09-07, finding #2): ONE shared
  // timestamp for every item this section renders — all of them come
  // from this SAME batch of GETs, so they were all genuinely last
  // checked together. Set ONLY after every GET below resolves
  // successfully (Promise.all rejects the whole batch on any single
  // failure, landing in the catch below, which never touches this) — see
  // `PersonaSourceStillProgressCard`'s own top doc comment.
  const [lastCheckedAtMs, setLastCheckedAtMs] = useState<number | null>(null);
  const { normal: readyNormalItems, identityFailed: identityFailedItems } = splitGate1ReadyByIdentity(readyItems);

  const load = useCallback(async () => {
    try {
      const [processing, needsFrameSelection, candidatesPending, ready, approved, rejected] = await Promise.all([
        api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=${GATE1_PROCESSING_STATUSES}`),
        api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=${GATE1_NEEDS_FRAME_SELECTION_STATUS}`),
        api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=${GATE1_CANDIDATES_PENDING_STATUS}`),
        api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=${GATE1_REVIEWABLE_WITH_WARNING_STATUSES}`),
        // REVIEWS-as-action-queue fix, extended (2026-09 follow-up):
        // `activeOnly=true` is the ONE truthful server-side definition of
        // "active" for an approved artifact — it now also covers the
        // brief transient window between Approve and a bound Gate 2 video
        // (a genuine, durable `gate2AutoStartRequestedAt` intent with no
        // video bound yet), so this single fetch is enough for BOTH the
        // "Approved — starting Gate 2…" and "active Gate 2 workflow"
        // sections below — no second, unfiltered fetch that could leak an
        // old approved artifact with no genuine current activity.
        api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=persona_swap_approved&activeOnly=true`),
        api.get<{ artifacts: PersonaSourceStillSummary[] }>(`/videos/${tenantId}/persona-source-stills?status=persona_swap_rejected`),
      ]);
      setProcessingItems(processing.artifacts);
      setNeedsFrameSelectionItems(needsFrameSelection.artifacts);
      setCandidatesPendingItems(candidatesPending.artifacts);
      setReadyItems(ready.artifacts);
      setRejectedItems(rejected.artifacts.filter((item) => item.progress.rejectedWithFrameChoice === true));
      const { starting, activeWorkflow } = selectGate1ApprovedBuckets(approved.artifacts);
      setStartingItems(starting);
      setApprovedItems(activeWorkflow);
      setLastCheckedAtMs(Date.now());
      // A transient failed poll must not leave this section stuck on its
      // error banner forever: the next successful poll (or push) clears it,
      // so the list recovers on its own — no manual page refresh needed.
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load Gate 1 reviews.");
    }
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    function tick(): void {
      load().catch(() => undefined);
    }
    tick();
    const interval = setInterval(() => {
      if (!cancelled) tick();
    }, GATE1_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [load]);

  useReviewEventStream(tenantId, load);
  // FIX-021: refresh immediately (read-only, same `load`) when the page returns to the foreground after Android/browser background or screen-off.
  useForegroundResume(load);

  async function decide(id: string, decision: "approve" | "reject", options: { readonly identityFailureUserOverride?: boolean } = {}): Promise<void> {
    setBusyId(id);
    try {
      await api.post(`/videos/${tenantId}/persona-source-stills/${id}/decide`, csrfToken, {
        decision,
        reviewedBy: user?.email ?? "owner",
        ...(options.identityFailureUserOverride === true ? { identityFailureUserOverride: true } : {}),
      });
      await load(); // never assume success from the click alone — re-read durable backend state
    } catch (decideError) {
      setError(decideError instanceof ApiError ? decideError.message : "Decision failed.");
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Gate 1 Reviews "Remove" (2026-09-16): dismisses a stuck/old card from
   * this list ONLY — never deletes the artifact, its media, its audit
   * trail, or its cost records (see `RemovePersonaSourceStillFromReviews`'s
   * own doc comment). Not a decision (approve/reject); no confirmation
   * beyond the browser's own `confirm()`, since it's non-destructive and
   * fully reversible by starting a new attempt.
   */
  async function remove(id: string): Promise<void> {
    if (!window.confirm("Remove this card from Reviews? It will no longer appear here, but nothing is deleted.")) {
      return;
    }
    setBusyId(id);
    try {
      await api.post(`/videos/${tenantId}/persona-source-stills/${id}/remove-from-reviews`, csrfToken, { removedBy: user?.email ?? "owner" });
      await load(); // never assume success from the click alone — re-read durable backend state
    } catch (removeError) {
      setError(removeError instanceof ApiError ? removeError.message : "Remove failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <div className="error-banner">{error}</div>;

  return (
    <div>
      <h3>Processing</h3>
      {processingItems.length === 0 ? (
        <p className="muted">Nothing currently processing.</p>
      ) : (
        processingItems.map((item) => (
          <div key={item.id} style={{ marginBottom: 8 }}>
            <ArtifactIdentity
              id={item.id}
              thumbnailSrc={item.swappedFrameGcsUri !== null ? gate1PreviewSrc(tenantId, item.id, item.updatedAt, item.swappedFrameChecksumSha256) : null}
              label={new Date(item.createdAt).toLocaleDateString()}
            />
            <PersonaSourceStillProgressCard progress={item.progress} lastCheckedAtMs={lastCheckedAtMs} tenantId={tenantId} onRefresh={load} />
            {/* A cleanup-failed card carries its own Retry cleanup / Remove actions. */}
            {item.progress.displayStatus !== "needs_attention" && (
              <button disabled={busyId === item.id} onClick={() => void remove(item.id)} style={{ marginTop: 4 }}>
                Remove
              </button>
            )}
          </div>
        ))
      )}

      {needsFrameSelectionItems.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>Needs frame selection</h3>
          {needsFrameSelectionItems.map((item) => (
            <div key={item.id} style={{ marginBottom: 8 }}>
              <ArtifactIdentity id={item.id} thumbnailSrc={null} label={new Date(item.createdAt).toLocaleDateString()} />
              <PersonaSourceStillProgressCard progress={item.progress} lastCheckedAtMs={lastCheckedAtMs} tenantId={tenantId} onRefresh={load} />
              <button disabled={busyId === item.id} onClick={() => void remove(item.id)} style={{ marginTop: 4 }}>
                Remove
              </button>
            </div>
          ))}
        </>
      )}

      {candidatesPendingItems.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Multiple edited images produced — choose one</h3>
          {candidatesPendingItems.map((item) => (
            <VisualModificationCandidatesCard key={item.id} tenantId={tenantId} item={item} onDecided={load} />
          ))}
        </>
      )}

      <h3 style={{ marginTop: 20 }}>Ready for review</h3>
      {readyNormalItems.length === 0 ? (
        <p className="muted">No Gate 1 items waiting for review.</p>
      ) : (
        <div className="grid grid-3">
          {readyNormalItems.map((item) => (
            <div key={item.id} className="card">
              <img className="preview-image" src={gate1PreviewSrc(tenantId, item.id, item.updatedAt, item.swappedFrameChecksumSha256)} alt="Gate 1 preview" />
              <p className="muted" style={{ marginTop: 8 }}>{item.progress.requestedChangesDescription ?? "No visual modifications requested"}</p>
              <p className="muted">Tracked cost: ${item.progress.trackedCostUsd.toFixed(2)}</p>
              {/* Live-reproduced bug (2026-09-05): the current canonical
                  image has not yet been confirmed delivered to a browser —
                  the picture above may still be an earlier stage's stale
                  bytes (a stale <img> element reuse can serve those without
                  a network refetch even with no-store). Approve is refused
                  server-side either way (Gate 1 Actual-Image-Delivery
                  Invariant), but the user should see this BEFORE clicking,
                  not discover it as an approval error. */}
              {!item.previewDelivered && (
                <p className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
                  ⚠ This preview may be out of date — the image above might not reflect the latest edit yet. Refresh the page before approving; approval will be refused until the
                  current image has actually loaded.
                </p>
              )}
              {/* FIX-010 correction (2026-09): these two statuses previously meant the existing image never reached Gate 1 review at all. Shown here (not hidden), with a truthful warning, so the USER decides. */}
              {item.status === "persona_swap_visual_modification_failed" && (
                <p className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
                  ⚠ The requested visual modification could not be applied
                  {item.stillVisualModificationLastProviderError ? `: ${item.stillVisualModificationLastProviderError}` : "."} The image shown is the version BEFORE that
                  modification was attempted.
                </p>
              )}
              {item.status === "persona_swap_visual_modification_submission_uncertain" && (
                <p className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
                  ⚠ It is uncertain whether the requested visual modification was submitted to the provider — automated recovery is still trying in the background. The image
                  shown is the version BEFORE that modification was attempted.
                </p>
              )}
              {item.stillVisualModificationIdentityGuardReason && <p className="muted">{item.stillVisualModificationIdentityGuardReason}</p>}
              {/* FIX-010 correction (2026-09): this per-target manifest was already persisted but never rendered in Gate 1 Reviews — a failed/inconclusive requested-change finding was invisible here. */}
              {describeRequestedChangeFindings(item.stillRequestedChangeVerification).map((finding) => (
                <p key={finding} className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
                  ⚠ {finding}
                </p>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button className="success" disabled={busyId === item.id} onClick={() => void decide(item.id, "approve")}>
                  Approve
                </button>
                <button className="danger" disabled={busyId === item.id} onClick={() => void decide(item.id, "reject")}>
                  Reject
                </button>
                <button disabled={busyId === item.id} onClick={() => void remove(item.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {identityFailedItems.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Identity check failed</h3>
          <p className="muted">
            These Gate 1 results failed identity verification on the final image, so they are NOT shown as normal successful items. Each is listed here — nothing is hidden — and you can choose
            another frame to run Gate 1 again, approve the result manually with "Approve anyway", or reject it.
          </p>
          {identityFailedItems.map((item) => (
            <div key={item.id} style={{ marginBottom: 8 }} data-testid={`gate1-identity-failed-item-${item.id}`}>
              <PersonaSourceStillProgressCard progress={item.progress} lastCheckedAtMs={lastCheckedAtMs} tenantId={tenantId} onRefresh={load} />
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                  className="success"
                  disabled={busyId === item.id}
                  onClick={() => {
                    if (!window.confirm("The identity check FAILED for this result. Approve it anyway? Your manual approval is recorded, and the result continues to Gate 2 like any approved Gate 1 result.")) return;
                    void decide(item.id, "approve", { identityFailureUserOverride: true });
                  }}
                >
                  Approve anyway
                </button>
                <button className="danger" disabled={busyId === item.id} onClick={() => void decide(item.id, "reject")}>
                  Reject
                </button>
                <button disabled={busyId === item.id} onClick={() => void remove(item.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {rejectedItems.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Rejected</h3>
          <p className="muted">
            You rejected these Gate 1 results. They stay here until you decide: choose another frame to run Gate 1 again, or remove the card (nothing is deleted).
          </p>
          {rejectedItems.map((item) => (
            <div key={item.id} style={{ marginBottom: 8 }} data-testid={`gate1-rejected-item-${item.id}`}>
              <PersonaSourceStillProgressCard progress={item.progress} lastCheckedAtMs={lastCheckedAtMs} tenantId={tenantId} onRefresh={load} />
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button disabled={busyId === item.id} onClick={() => void remove(item.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {startingItems.length > 0 && (
        <>
          <h3 style={{ marginTop: 20 }}>Approved — starting Gate 2…</h3>
          {startingItems.map((item) => (
            <div key={item.id} style={{ marginBottom: 8 }}>
              <ArtifactIdentity
                id={item.id}
                thumbnailSrc={item.swappedFrameGcsUri !== null ? gate1PreviewSrc(tenantId, item.id, item.updatedAt, item.swappedFrameChecksumSha256) : null}
                label="Approved — starting Gate 2…"
              />
            </div>
          ))}
        </>
      )}

      <h3 style={{ marginTop: 20 }}>Approved (active Gate 2 workflow)</h3>
      {approvedItems.length === 0 ? (
        <p className="muted">No approved Gate 1 artifact currently has an active Gate 2 workflow.</p>
      ) : (
        approvedItems.map((item) => (
          <div key={item.id} style={{ marginBottom: 8 }}>
            <ArtifactIdentity
              id={item.id}
              thumbnailSrc={item.swappedFrameGcsUri !== null ? gate1PreviewSrc(tenantId, item.id, item.updatedAt, item.swappedFrameChecksumSha256) : null}
              label="Gate 2 (below) is active for this artifact"
            />
            <PersonaSourceStillProgressCard progress={item.progress} title="GATE 1 — COMPLETE LOOK" lastCheckedAtMs={lastCheckedAtMs} tenantId={tenantId} onRefresh={load} />
          </div>
        ))
      )}
    </div>
  );
}

/** Two-fixes-follow-up (2026-09, refresh-cadence correction): product requirement is exactly 15s auto-refresh for Gate 1/Gate 2/Final Review active status — was 5s, inconsistent with Gate 1's own cadence for no real reason. Also drives the Final Review section (same Gate2Section load cycle). */
const GATE2_REFRESH_INTERVAL_MS = 15_000;
/** Real `VideoStatus` values — never invented — bucketed into the two visible "still working" phases the product spec asks for. */
const CREATING_VIDEO_STATUSES = new Set(["draft", "generating"]);
const FINALIZING_STATUSES = new Set(["approved", "upscaling", "subtitle_processing"]);
/** Final Review gate (2026-09): the real final asset already exists — waiting on a human's LAST Approve/Reject decision, distinct from Gate 2's own "ready_for_review" bucket above. */
const READY_FOR_FINAL_REVIEW_STATUSES = new Set(["ready_for_final_review"]);
/**
 * REVIEWS-as-action-queue guarantee (2026-09 follow-up, mirrors Gate 1's
 * own server-side `activeOnly` filter — see `persona-source-still-routes.ts`'s
 * `handleList`): the exact three real `VideoStatus` values that are
 * NEVER shown in REVIEWS regardless of anything else — a video reaching
 * any of these is DONE needing action from Reviews' point of view (a
 * true technical failure, an explicit reject, or a fully finalized
 * deliverable) and belongs only in HISTORY from here on. Named/exported
 * explicitly (rather than left as "whatever doesn't match the three
 * positive buckets below") so this exclusion is a stated guarantee, not
 * an accident of which buckets happen to be rendered.
 */
export const GATE2_TERMINAL_STATUSES = new Set(["failed", "rejected", "finalized", "final_review_rejected"]);

/**
 * REVIEWS-as-action-queue guarantee, Gate 2 side: pure bucketing so it's
 * unit-testable without a DOM — proves every video lands in AT MOST one
 * of the three "currently active/actionable" buckets, and a video whose
 * status is in `GATE2_TERMINAL_STATUSES` lands in NONE of them (excluded
 * from REVIEWS entirely; it remains fully visible in HISTORY, which reads
 * the same unfiltered video list this function's caller fetches — this
 * function only decides what REVIEWS itself renders).
 */
/**
 * Gate 2 review-authority correction (2026-09 follow-up): the human, not
 * an automated verifier, makes Gate 2's accept/reject call — so a
 * `"forbidden_identity"`/`"requested_change_failed"` outcome must never
 * be silently dropped just because it no longer blocks review. Pure and
 * unit-testable: returns the warning strings to show alongside the real
 * video, or an empty array for a clean `"reviewable"` result (or no
 * payload at all — a video with no visual modification requested never
 * has one).
 */
export function describeGate2Warnings(visualModificationOutcome: string | null | undefined): readonly string[] {
  switch (visualModificationOutcome) {
    case "forbidden_identity":
      return ["Identity change detected — an automated check flagged that this person may look different than expected. Review the video yourself before deciding."];
    case "requested_change_failed":
      return ["Requested visual change may not match — an automated check could not confirm the requested clothing color or pendant was applied correctly (or that the original garment design was preserved). Review the video yourself before deciding."];
    // FIX-010 correction (2026-09): these two outcomes previously fell
    // through to the `default: []` case below — reaching Gate 2 review
    // with NO warning shown at all, even though both are real, advisory
    // (non-blocking) verifier findings, same as the two above.
    case "forbidden_technical":
      return ["Technical issue detected — an automated check flagged a possible technical problem with this edit (e.g. an artifact unrelated to the requested change). Review the video yourself before deciding."];
    // Gate 2 truthfulness micro-closure (2026-09): `"verification_inconclusive"`
    // is deliberately NOT handled here anymore — it is genuinely ambiguous
    // from this bare string alone (it means EITHER the visual-modification
    // Identity Guard's own call failed, OR a per-target requested-change
    // verifier was inconclusive — see ApplyVisualModificationToCanonicalOutput's
    // own outcomeKind derivation). Collapsing both into one generic
    // "...whether the requested change was applied" message misdescribed
    // a pure identity-guard call failure as if it were about the requested
    // change. See `describeVerificationInconclusiveWarning`, which has the
    // full payload needed to attribute this outcome to the RIGHT check.
    default:
      return [];
  }
}

/**
 * Gate 2 truthfulness micro-closure (2026-09, post-Production-verification):
 * the ONE place `visualModificationOutcome === "verification_inconclusive"`
 * is ever turned into reviewer-facing text — replaces the generic,
 * possibly-misattributed message `describeGate2Warnings` used to return
 * for this outcome. Attribution is never guessed: `requestedChangeVerification`
 * is the ACTUAL persisted per-target manifest, so "does any target carry
 * outcome==='inconclusive'" is concrete evidence the requested-change
 * verifier (not the identity guard) is the reason, not an inference from
 * a bare true/false/null. Only when NO target is inconclusive AND a real
 * identity-guard reason is on file does this attribute the outcome to the
 * Identity Guard's own call failure — the exact scenario
 * `visualModificationIdentityReason` exists to describe truthfully.
 */
export function describeVerificationInconclusiveWarning(payload: GateTwoReviewPayload): readonly string[] {
  if (payload.visualModificationOutcome !== "verification_inconclusive") {
    return [];
  }
  const hasInconclusiveRequestedChangeTarget = (payload.requestedChangeVerification ?? []).some((result) => result.outcome === "inconclusive");
  if (!hasInconclusiveRequestedChangeTarget && payload.visualModificationIdentityReason !== null) {
    return [`Visual-edit identity verification inconclusive: ${payload.visualModificationIdentityReason}`];
  }
  return ["Verification inconclusive — an automated check could not reach a reliable answer about whether the requested change was applied. Review the video yourself before deciding."];
}

/**
 * FIX-010 correction (2026-09): the per-target requested-change
 * verification manifest was already persisted and already reached this
 * payload (`requestedChangeVerification`), but REVIEWS only ever showed
 * its count ("N recorded") — a failed/inconclusive per-target finding was
 * invisible unless a reviewer went digging elsewhere. Returns only the
 * non-clean (not "reviewable"/"passed") findings, since a clean result
 * needs no extra attention beyond the count already shown.
 */
export function describeRequestedChangeFindings(requestedChangeVerification: GateTwoReviewPayload["requestedChangeVerification"]): readonly string[] {
  if (requestedChangeVerification === null || requestedChangeVerification === undefined) {
    return [];
  }
  return requestedChangeVerification.filter((result) => result.outcome !== "verified").map((result) => `${result.target}: ${result.outcome} — ${result.finalReason}`);
}

/**
 * FIX-010 focused correction (2026-09): these findings were always
 * computed (by `VerifyPersonaVideoRecreationOutput` for the generation
 * stage's source-fidelity/target-persona-identity checks, and by the new
 * `computeVisualModificationTemporalFidelity` for the visual-modification
 * stage's own duration/geometry check) but never reached this review
 * screen — a reviewer could approve a video whose face didn't match the
 * target persona's reference identity, or whose visual edit measurably
 * changed timing/geometry, with zero indication either concern was ever
 * detected. Kept as its own function (never merged into
 * `describeGate2Warnings`) since these come from a genuinely different
 * verifier/stage and must remain independently attributable — a clean
 * clothing/pendant result must never visually erase an identity or
 * temporal-fidelity concern, or vice versa.
 */
/**
 * Immutable preview URLs (2026-09-05 follow-up). Live-reproduced bug
 * (real paid E2E run, artifact 9048c5b4-...): `swappedFrameGcsUri` is
 * overwritten IN PLACE at the SAME GCS object path across pipeline
 * stages (pendant removal -> clothing/pendant addition), and `GET
 * .../preview` correctly streams fresh bytes every time — but every
 * caller of this URL used the exact same string across renders, so the
 * browser's own `<img>` element reuse never issued a new network
 * request at all once one had already loaded for that artifact id. The
 * user reviewed a still image from an EARLIER stage with no indication
 * anything had changed since.
 *
 * `checksumSha256` (the artifact's `swappedFrameChecksumSha256` — the
 * SAME field the Gate 1 Actual-Image-Delivery Invariant already
 * authoritatively tracks) is what actually makes this a genuinely
 * IMMUTABLE URL, per the two-part contract: (1) a new version gets a
 * genuinely new URL — a different checksum, since any real content
 * change necessarily produces one; (2) this exact URL's meaning never
 * changes going forward — the server (`handlePreview`,
 * persona-source-still-routes.ts) 404s rather than ever serving
 * different bytes under a stale checksum, and serves `cache-control:
 * public, max-age=31536000, immutable` when it matches, so the browser
 * can cache it forever without ever re-validating. Falls back to a
 * `updatedAt`-versioned, NON-immutable URL (`cache-control: no-store`
 * server-side) only for the narrow window before a checksum exists yet
 * (still processing) — forces a refetch on change, same as before this
 * follow-up, just not eligible for permanent caching.
 */
export function gate1PreviewSrc(tenantId: string, artifactId: string, updatedAt: string, checksumSha256: string | null): string {
  const base = `/videos/${tenantId}/persona-source-stills/${artifactId}/preview`;
  return checksumSha256 !== null ? `${base}?checksum=${encodeURIComponent(checksumSha256)}` : `${base}?v=${encodeURIComponent(updatedAt)}`;
}

/**
 * SAME bug class and SAME immutable-URL fix as `gate1PreviewSrc` above.
 * `checksumSha256` is the video's `previewChecksumSha256` (the SAME
 * `currentAuthoritativePreviewChecksumSha256` value Gate 2's Actual-
 * Video-Delivery Invariant already tracks — the subtitle-burned Gate 2
 * preview's own checksum when one exists, else the canonical output's).
 */
export function gate2PreviewSrc(tenantId: string, videoId: string, updatedAt: string, checksumSha256: string | null): string {
  const base = `/videos/${tenantId}/${videoId}/preview`;
  return checksumSha256 !== null ? `${base}?checksum=${encodeURIComponent(checksumSha256)}` : `${base}?v=${encodeURIComponent(updatedAt)}`;
}

/** Final Review gate (2026-09): the REAL final asset (1080p, subtitles burned, final audio) — the SAME strict `.../final` route `handleFinalMedia` always used, now also servable during `ready_for_final_review` (see that route's own doc comment), never the Gate 2 staging preview. */
export function finalMediaSrc(tenantId: string, videoId: string, updatedAt: string): string {
  return `/videos/${tenantId}/${videoId}/final?v=${encodeURIComponent(updatedAt)}`;
}

/**
 * FIX-064 SP-2: the post-generation caption check of a pre-cleaned RP video runs in the background. Gate 2 shows it while running, a clear warning
 * when it flagged possible captions (nothing is cleaned automatically — the owner decides), and the same warning on the Final Review card.
 */
export function describeBackgroundCaptionCheck(payload: GateTwoReviewPayload | undefined): readonly { readonly kind: "info" | "warning"; readonly text: string }[] {
  const check = payload?.backgroundCaptionCheck ?? null;
  if (check === null) return [];
  if (check.status === "running") return [{ kind: "info", text: "Caption check is still running in the background (the source was pre-cleaned). A warning appears here if it finds captions." }];
  if (check.status === "clean") return [{ kind: "info", text: "Background caption check: no captions found." }];
  if (check.status === "captions_present") {
    return [
      {
        kind: "warning",
        text: `Background caption check found possible captions/text in this video${check.reason !== null ? `: ${check.reason}` : ""}. Nothing was cleaned automatically — look at the video and reject or regenerate if captions are visible.`,
      },
    ];
  }
  return [];
}

export function describeIdentityAndTemporalFidelityWarnings(payload: GateTwoReviewPayload): readonly string[] {
  const warnings: string[] = [];
  // Part C3 correction (final pre-E2E closure, 2026-09): `null` ("the
  // check never reached a definite verdict — a verifier call failure")
  // must NEVER be labeled the same as a definite `false` ("the check
  // completed and found a real mismatch") — these are two different
  // findings the human needs to read differently. `null` only ever shows
  // this distinct "inconclusive" wording, and only when there is an
  // actual reason on file describing why (never a bare warning for a
  // video that simply predates this field, which also reads as `null`
  // but with no real reason to show).
  if (payload.targetPersonaIdentityPreserved === false) {
    warnings.push(`Face/identity concern: ${payload.targetPersonaIdentityReason ?? "the recreated face may not match the target persona's reference identity"}`);
  } else if (payload.targetPersonaIdentityPreserved === null && payload.targetPersonaIdentityReason !== null) {
    warnings.push(`Target-persona identity verification inconclusive: ${payload.targetPersonaIdentityReason}`);
  }
  for (const mismatch of payload.fidelityMismatches) {
    warnings.push(`Source fidelity: ${mismatch}`);
  }
  for (const mismatch of payload.visualModificationTemporalFidelityMismatches ?? []) {
    warnings.push(`Visual-edit timing/geometry: ${mismatch}`);
  }
  // Gate 2 truthfulness micro-closure (2026-09, post-Production-verification):
  // the raw temporal-fidelity mismatch above describes the PROVIDER's raw
  // output only — it never says whether the CANONICAL candidate the
  // reviewer is actually looking at still has that wrong geometry. Never
  // hides that raw finding (kept, unchanged, above); this ADDS the
  // clarifying outcome so "frame geometry was not preserved" is never
  // left to imply the final video is still wrong when it was already
  // corrected. `"not_needed"`/`"not_evaluated"`/`null` produce no extra
  // line at all — never fabricated as either a success or a failure.
  if (payload.visualModificationGeometryNormalizationStatus === "normalized") {
    warnings.push(
      `Geometry corrected: ${payload.visualModificationGeometryNormalizationDetail ?? "the raw visual-edit output's geometry drift was successfully normalized back to the pre-edit canonical dimensions, independently verified, before this video reached review."}`,
    );
  } else if (payload.visualModificationGeometryNormalizationStatus === "normalization_failed") {
    warnings.push(
      `Geometry unresolved: ${payload.visualModificationGeometryNormalizationDetail ?? "an attempt to correct the visual-edit output's geometry drift could not be verified — the raw provider output is being shown and this remains an unresolved warning."}`,
    );
  }
  if (payload.visualModificationLipSyncAdvisory !== null) {
    warnings.push(payload.visualModificationLipSyncAdvisory);
  }
  return warnings;
}

function BackgroundCaptionCheckLines({ payload, onlyWarnings = false }: { readonly payload: GateTwoReviewPayload | undefined; readonly onlyWarnings?: boolean }) {
  const lines = describeBackgroundCaptionCheck(payload).filter((line) => !onlyWarnings || line.kind === "warning");
  return (
    <>
      {lines.map((line) =>
        line.kind === "warning" ? (
          <p key={line.text} className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
            ⚠ {line.text}
          </p>
        ) : (
          <p key={line.text} className="muted">
            {line.text}
          </p>
        ),
      )}
    </>
  );
}

export function selectGate2ReviewBuckets(videos: readonly VideoHistoryRow[]): {
  readonly creating: readonly VideoHistoryRow[];
  readonly readyForReview: readonly VideoHistoryRow[];
  readonly finalizing: readonly VideoHistoryRow[];
  readonly readyForFinalReview: readonly VideoHistoryRow[];
} {
  return {
    creating: videos.filter((v) => CREATING_VIDEO_STATUSES.has(v.status)),
    readyForReview: videos.filter((v) => v.status === "ready_for_review"),
    finalizing: videos.filter((v) => FINALIZING_STATUSES.has(v.status)),
    readyForFinalReview: videos.filter((v) => READY_FOR_FINAL_REVIEW_STATUSES.has(v.status)),
  };
}

/** `phase` picks which real checklist to render — pre-review ("creating") or post-approval ("finalizing"); see `VideoGateProgressCard`'s own doc comment for why these are never conflated. */
function VideoProgressStrip({ tenantId, video, phase }: { readonly tenantId: string; readonly video: VideoHistoryRow; readonly phase: "creating" | "finalizing" }) {
  const [progress, setProgress] = useState<VideoProgress | null>(null);
  // Truthful freshness correction (2026-09-07, finding #2): set ONLY in
  // the success branch below, never in .catch — see
  // `VideoGateProgressCard`'s own top doc comment.
  const [lastCheckedAtMs, setLastCheckedAtMs] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    function tick(): void {
      api
        .get<VideoProgress>(`/videos/${tenantId}/${video.id}/progress`)
        .then((result) => {
          if (!cancelled) {
            setProgress(result);
            setLastCheckedAtMs(Date.now());
          }
        })
        .catch(() => undefined);
    }
    tick();
    const interval = setInterval(tick, GATE2_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [tenantId, video.id]);

  if (progress === null) {
    return (
      <div className="progress-card">
        <div className="progress-header">
          <span className="progress-stage-label">{video.humanReadableName}</span>
        </div>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const milestones = phase === "creating" ? progress.milestones.preReview : (progress.milestones.finalizing ?? []);
  const title = phase === "creating" ? "GATE 2 — CREATING VIDEO" : "FINALIZING VIDEO";
  // Finalizing's own "start time" is the approval moment, not video creation — video.approvedAt isn't on VideoHistoryRow, so videoCreatedAt is used consistently for both; the elapsed label is still real wall-clock time, just measured from video creation either way.
  return (
    <VideoGateProgressCard
      title={`${title} — ${video.humanReadableName}`}
      progress={progress}
      milestones={milestones}
      startedAtIso={progress.videoCreatedAt}
      lastCheckedAtMs={lastCheckedAtMs}
    />
  );
}

/**
 * Multi-output review-authority correction (2026-09): when a generation
 * provider returns more than one output, none is ever auto-selected or
 * auto-rejected (see `DecideVideoOutputCandidate`'s own doc comment on the
 * API side) — this card is the ONLY place that anomaly becomes visible and
 * actionable to a human, directly inside the existing REVIEWS "Creating
 * video" section (never a separate page, never a Manual-Reconciliation-only
 * dead end). Every candidate is independently previewable
 * (`.../output-candidates/{index}/preview`) so the user can actually watch
 * each option before choosing — "Use this output" sends the caller's own
 * `candidateIndex` verbatim, never a guessed/defaulted one. This is
 * deliberately NOT Gate 2 approval: selecting a candidate only resolves
 * WHICH provider output becomes the canonical one; the unmodified existing
 * pipeline (verify -> ready_for_review -> the ordinary Gate 2 approve/
 * reject above) still runs afterward, untouched by this component.
 */
function MultiOutputCandidatesCard({
  tenantId,
  video,
  data,
  onDecided,
}: {
  readonly tenantId: string;
  readonly video: VideoHistoryRow;
  readonly data: OutputCandidatesResponse;
  readonly onDecided: () => Promise<void>;
}) {
  const { user, csrfToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(body: { readonly decision: "select"; readonly candidateIndex: number } | { readonly decision: "reject" }): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/videos/${tenantId}/${video.id}/output-candidates/decide`, csrfToken, { ...body, reviewedBy: user?.email ?? "owner" });
      // Never assume success from the click alone, and never remove this
      // card ourselves — re-read durable backend state; the card
      // disappears only once the server itself reports zero pending
      // candidates for this video.
      await onDecided();
    } catch (decideError) {
      setError(decideError instanceof ApiError ? decideError.message : "Decision failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" data-testid={`output-candidates-${video.id}`}>
      <h3>{video.humanReadableName} — multiple outputs produced</h3>
      <p className="muted">The generation provider returned more than one result. Watch each option, then choose which one to continue with, or reject all.</p>
      {error && <div className="error-banner">{error}</div>}
      <div className="grid grid-3">
        {data.candidates.map((candidate) => (
          <div key={candidate.index} style={{ marginBottom: 8 }}>
            <p>{`Option ${candidate.index + 1}`}</p>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- provider-generated video, no caption track exists */}
            <video
              className="preview-player"
              controls
              src={`/videos/${tenantId}/${video.id}/output-candidates/${candidate.index}/preview`}
              data-testid={`output-candidate-preview-${video.id}-${candidate.index}`}
            />
            <p className="muted">{candidate.sizeBytes} bytes</p>
            <button className="success" disabled={busy} onClick={() => void decide({ decision: "select", candidateIndex: candidate.index })}>
              Use this output
            </button>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12 }}>
        <button className="danger" disabled={busy} onClick={() => void decide({ decision: "reject" })}>
          Reject all
        </button>
      </div>
    </div>
  );
}

function Gate2Section({ tenantId }: { readonly tenantId: string }) {
  const { user, csrfToken } = useAuth();
  const [allVideos, setAllVideos] = useState<VideoHistoryRow[]>([]);
  const [reviewPayloads, setReviewPayloads] = useState<Record<string, GateTwoReviewPayload>>({});
  const [progressByVideo, setProgressByVideo] = useState<Record<string, VideoProgress>>({});
  // Multi-output review-authority correction: only ever populated for a
  // video that genuinely has a pending candidate decision (non-empty
  // `candidates`) — the ordinary case (no anomaly, or already decided) is
  // simply absent from this map, so nothing new renders.
  const [candidatesByVideo, setCandidatesByVideo] = useState<Record<string, OutputCandidatesResponse>>({});
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const historyResult = await api.get<{ videos: VideoHistoryRow[] }>(`/videos/${tenantId}?limit=100`);
      setAllVideos(historyResult.videos);
      const waiting = historyResult.videos.filter((v) => v.status === "ready_for_review");
      // FIX-064 SP-2: the Final Review card shows a background caption-check flag too, so its payload is fetched as well (graceful when unavailable).
      const finalReviewWaiting = historyResult.videos.filter((v) => READY_FOR_FINAL_REVIEW_STATUSES.has(v.status));
      // Multi-output review-authority correction: a pending multi-output
      // decision can only exist BEFORE a video reaches ready_for_review —
      // once it's approved/rejected one way or another, the anomaly (if
      // there ever was one) has already been resolved. Scoping the fetch
      // to the same "creating" statuses REVIEWS already renders means this
      // never touches ready_for_review/finalizing behavior at all.
      const creatingVideos = historyResult.videos.filter((v) => CREATING_VIDEO_STATUSES.has(v.status));
      const payloads: Record<string, GateTwoReviewPayload> = {};
      const progresses: Record<string, VideoProgress> = {};
      const candidates: Record<string, OutputCandidatesResponse> = {};
      await Promise.all([
        ...waiting.map(async (v) => {
          try {
            payloads[v.id] = await api.get<GateTwoReviewPayload>(`/videos/${tenantId}/${v.id}/gate2-review`);
          } catch {
            // No visual modification on this video, or not yet available — Gate 2 approval doesn't strictly require this payload.
          }
          progresses[v.id] = await api.get<VideoProgress>(`/videos/${tenantId}/${v.id}/progress`);
        }),
        ...finalReviewWaiting.map(async (v) => {
          try {
            payloads[v.id] = await api.get<GateTwoReviewPayload>(`/videos/${tenantId}/${v.id}/gate2-review`);
          } catch {
            // Optional on Final Review: only the background caption-check line reads it.
          }
        }),
        ...creatingVideos.map(async (v) => {
          try {
            const result = await api.get<OutputCandidatesResponse>(`/videos/${tenantId}/${v.id}/output-candidates`);
            if (result.candidates.length > 0) {
              candidates[v.id] = result;
            }
          } catch {
            // Same graceful-degradation doctrine as the gate2-review payload
            // above — this endpoint being unavailable must never block
            // ordinary Gate 2 rendering.
          }
        }),
      ]);
      setReviewPayloads(payloads);
      setProgressByVideo(progresses);
      setCandidatesByVideo(candidates);
      // A transient failed poll must not leave Gate 2 stuck on its error
      // banner: the next successful poll (or push) clears it — no page refresh.
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof ApiError ? loadError.message : "Failed to load Gate 2 reviews.");
    }
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    function tick(): void {
      load().catch(() => undefined);
    }
    tick();
    const interval = setInterval(() => {
      if (!cancelled) tick();
    }, GATE2_REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [load]);

  useReviewEventStream(tenantId, load);
  // FIX-021: refresh immediately (read-only, same `load`) when the page returns to the foreground after Android/browser background or screen-off.
  useForegroundResume(load);

  async function decide(videoId: string, decision: "approve" | "reject", options: { readonly secondTry?: boolean } = {}): Promise<void> {
    const previewGenerationJobId = progressByVideo[videoId]?.generationJobId;
    if (previewGenerationJobId === null || previewGenerationJobId === undefined) return;
    setBusyId(videoId);
    try {
      await api.post(`/videos/${tenantId}/${videoId}/decide`, csrfToken, { decision, previewGenerationJobId, reviewedBy: user?.email ?? "owner", ...(options.secondTry === true ? { secondTry: true } : {}) });
      await load();
    } catch (decideError) {
      setError(decideError instanceof ApiError ? decideError.message : "Decision failed.");
    } finally {
      setBusyId(null);
    }
  }

  // Final Review gate (2026-09): a GENUINELY separate, later decision from
  // Gate 2's own `decide` above — no previewGenerationJobId concept here.
  async function decideFinal(videoId: string, decision: "approve" | "reject"): Promise<void> {
    setBusyId(videoId);
    try {
      await api.post(`/videos/${tenantId}/${videoId}/final-decide`, csrfToken, { decision, reviewedBy: user?.email ?? "owner" });
      await load();
    } catch (decideError) {
      setError(decideError instanceof ApiError ? decideError.message : "Final decision failed.");
    } finally {
      setBusyId(null);
    }
  }

  if (error) return <div className="error-banner">{error}</div>;

  const { creating, readyForReview, finalizing, readyForFinalReview } = selectGate2ReviewBuckets(allVideos);

  return (
    <div>
      <h3>Creating video</h3>
      {creating.length === 0 ? (
        <p className="muted">GATE 2 — WAITING. Nothing is currently being created (waiting for a Gate 1 approval to start one).</p>
      ) : (
        creating.map((video) => (
          <div key={video.id}>
            {candidatesByVideo[video.id] && <MultiOutputCandidatesCard tenantId={tenantId} video={video} data={candidatesByVideo[video.id]!} onDecided={load} />}
            <VideoProgressStrip tenantId={tenantId} video={video} phase="creating" />
          </div>
        ))
      )}

      <h3 style={{ marginTop: 20 }}>Ready for review</h3>
      {readyForReview.length === 0 ? (
        <p className="muted">No videos waiting for Gate 2 review.</p>
      ) : (
        readyForReview.map((video) => {
          const payload = reviewPayloads[video.id];
          return (
            <div key={video.id} className="card">
              <div style={{ display: "flex", gap: 20 }}>
                <video className="preview-player" controls src={gate2PreviewSrc(tenantId, video.id, video.updatedAt, video.previewChecksumSha256)} />
                <div style={{ flex: 1 }}>
                  <h3>{video.humanReadableName}</h3>
                  {payload &&
                    [
                      ...describeIdentityAndTemporalFidelityWarnings(payload),
                      ...describeGate2Warnings(payload.visualModificationOutcome),
                      ...describeVerificationInconclusiveWarning(payload),
                      ...describeRequestedChangeFindings(payload.requestedChangeVerification),
                      // FIX-010 correction (2026-09): a visual-modification
                      // provider failure that carried the existing
                      // unmodified video forward must reach the reviewer —
                      // never silently presented as if the edit succeeded.
                      ...(payload.visualModificationApplied === false && payload.visualModificationFailureReason !== null
                        ? [payload.visualModificationFailureReason]
                        : []),
                    ].map((warning) => (
                      <p key={warning} className="warning-banner" style={{ color: "var(--warning, #b45309)" }}>
                        ⚠ {warning}
                      </p>
                    ))}
                  <BackgroundCaptionCheckLines payload={payload} />
                  {payload && (
                    <div className="kv">
                      <dt>Requested changes</dt>
                      <dd>{JSON.stringify(payload.requestedModifications)}</dd>
                      <dt>Requested-change verification</dt>
                      <dd>{payload.requestedChanges.length} recorded</dd>
                      <dt>Unrequested reviewable changes</dt>
                      <dd>{payload.unrequestedReviewableChanges.length}</dd>
                    </div>
                  )}
                  <ReQcPanel tenantId={tenantId} videoId={video.id} />
                  <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                    <button className="success" disabled={busyId === video.id} onClick={() => void decide(video.id, "approve")}>
                      Approve
                    </button>
                    <button className="danger" disabled={busyId === video.id} onClick={() => void decide(video.id, "reject")}>
                      Reject
                    </button>
                    {typeof video.personaSourceStillId === "string" && (
                      <button disabled={busyId === video.id} onClick={() => void decide(video.id, "reject", { secondTry: true })} title="Reject this attempt and start one new Gate 2 attempt from the same approved Gate 1 image">
                        2nd try
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}

      <h3 style={{ marginTop: 20 }}>Finalizing video</h3>
      {finalizing.length === 0 ? (
        <p className="muted">Nothing currently finalizing (upscale / subtitle burn / final verification).</p>
      ) : (
        finalizing.map((video) => <VideoProgressStrip key={video.id} tenantId={tenantId} video={video} phase="finalizing" />)
      )}

      <h3 style={{ marginTop: 20 }}>Final Review</h3>
      {readyForFinalReview.length === 0 ? (
        <p className="muted">No videos waiting for Final Review.</p>
      ) : (
        readyForFinalReview.map((video) => (
          <div key={video.id} className="card">
            <div style={{ display: "flex", gap: 20 }}>
              <video className="preview-player" controls src={finalMediaSrc(tenantId, video.id, video.updatedAt)} />
              <div style={{ flex: 1 }}>
                {/* Gate 3 label fix (2026-09, finding #9): the prominent
                    stage title on the actual human-review surface must
                    identify the STAGE ("GATE 3 — FINAL REVIEW"), never
                    `humanReadableName` — a lineage/provenance string frozen
                    at video-creation time ("Gate 1 approved — <id>"), never
                    updated as the video moves through later stages. That
                    lineage text is still useful, so it remains as secondary
                    metadata below, exactly like Gate 2's own progress-strip
                    labels already show it. Gate 1's and Gate 2's own titles
                    are untouched — this correction is scoped to the Final
                    Review card only. */}
                <h3>GATE 3 — FINAL REVIEW</h3>
                <p className="muted" style={{ marginTop: -8 }}>{video.humanReadableName}</p>
                <p className="muted">This is the real final asset — 1080p, subtitles burned, final audio. Gate 2 approval is not Final Approval.</p>
                <BackgroundCaptionCheckLines payload={reviewPayloads[video.id]} onlyWarnings />
                <ReQcPanel tenantId={tenantId} videoId={video.id} />
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button className="success" disabled={busyId === video.id} onClick={() => void decideFinal(video.id, "approve")}>
                    Final Approve
                  </button>
                  <button className="danger" disabled={busyId === video.id} onClick={() => void decideFinal(video.id, "reject")}>
                    Final Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export function ReviewsTab({ tenantId }: { readonly tenantId: string }) {
  return (
    <div>
      {/* Owner t940u + 09:14 IL: one status bar, always shown - idle when no DR -> RE auto-production run is active, the run end to end while one is. */}
      <ReviewsAutoRunBar tenantId={tenantId} />
      {/* FIX-057 §16 owner t1835u: Turn off / Turn on next to the automation (its existing persisted pause). */}
      <ReviewsReAutoSwitch tenantId={tenantId} />
      {/* Owner t1149u + t1218u: the Telegram daily publish bars - one per channel, exactly like the video bar (idle line, step strip only while running). */}
      <ReviewsTelegramBar tenantId={tenantId} />
      {/* FIX-057 §16 owner t1835u: per-channel Turn off / Turn on of the daily automatic post (existing channel setting). */}
      <ReviewsTelegramAutoSwitch tenantId={tenantId} />
      {/* FIX-057 owner 14:16 IL / t1749u: Instagram auto-posting - one more bar in the same pattern (On/Off, last + next slot, Turn off / Turn on). */}
      <ReviewsIgAutopostBar tenantId={tenantId} />
      <div className="card">
        <h2>Gate 1 — Complete Look</h2>
        <Gate1Section tenantId={tenantId} />
      </div>
      <div className="card">
        <h2>Gate 2 — Video Review</h2>
        <Gate2Section tenantId={tenantId} />
      </div>
      <div className="card" data-testid="reviews-image-requests">
        <h2>Image requests</h2>
        <p className="muted">One item per Image request (1–15 images). A request stays here while it is generating, partially completed, or finished but not yet approved or rejected; after your final decision it moves to HISTORY.</p>
        <ImageRequestsList tenantId={tenantId} />
      </div>
      <div className="card">
        <h2>Manual Reconciliation</h2>
        <p className="muted">
          See <a href="/generate/monitor">MONITOR</a> for the current count of jobs needing manual reconciliation.
        </p>
      </div>
    </div>
  );
}
